/**
 * CDM Webhook Sources — Track 1.3: push-mode CDM ingest.
 *
 * External systems (ServiceNow, Jira, Datadog, GitHub Apps, custom
 * tools) POST records to /api/cdm/webhook/{name} with an HMAC-SHA256
 * X-Signature header. Each row here defines the JSON-path mapping from
 * the inbound body shape into CDM canonical fields + match keys.
 *
 * Editor pattern mirrors CdmSpecsPage.tsx — two-pane layout, JSON
 * textareas, inline test affordance (curl example, since we can't post
 * from the browser due to HMAC).
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Webhook, RefreshCw, ChevronLeft, Plus, Trash2, FileCode,
  Copy, RotateCw, Cable,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface WebhookSource {
  id: string
  name: string
  display_name: string
  archetype: string
  enabled: boolean
  secret_last4: string
  mapping: Record<string, string>
  match_keys_map: Record<string, string>
  id_path: string
  records_root: string
  created_at: string | null
  updated_at: string | null
}

const STARTER_MAPPING = {
  hostname: '$.computer.name',
  serial:   '$.computer.serial',
  os:       '$.computer.os',
}

const STARTER_MATCH_KEYS = {
  serial:   '$.computer.serial',
  hostname: '$.computer.name',
}

const ARCHETYPES = [
  'asset_server', 'asset_laptop', 'asset_mobile', 'asset_appliance',
  'user', 'identity', 'software_installation', 'network_object',
  'incident', 'vulnerability', 'change_request',
]

// Resolve a usable webhook URL for display in the curl example.
function getApiBase(): string {
  const fromEnv = (import.meta as any).env?.VITE_API_URL
  if (fromEnv) return String(fromEnv).replace(/\/$/, '')
  if (typeof window !== 'undefined') {
    return `${window.location.protocol}//${window.location.host}`
  }
  return ''
}

export default function CdmWebhooksPage() {
  const [sources, setSources] = useState<WebhookSource[]>([])
  const [loading, setLoading] = useState(false)
  const [editingName, setEditingName] = useState<string | null>(null)
  const [isNew, setIsNew] = useState(false)
  const [name, setName] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [archetype, setArchetype] = useState('asset_server')
  const [mappingText, setMappingText] = useState('')
  const [matchKeysText, setMatchKeysText] = useState('')
  const [idPath, setIdPath] = useState('$.id')
  const [recordsRoot, setRecordsRoot] = useState('$')
  const [enabled, setEnabled] = useState(true)
  const [message, setMessage] = useState('')
  const [revealedSecret, setRevealedSecret] = useState<string | null>(null)
  const [revealedFor, setRevealedFor] = useState<string | null>(null)

  const apiBase = getApiBase()

  const load = () => {
    setLoading(true)
    api.get('/api/cdm/webhook-sources')
      .then(r => setSources(r.data?.sources || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [])

  const startNew = () => {
    setIsNew(true)
    setEditingName(null)
    setRevealedSecret(null)
    setRevealedFor(null)
    setName('')
    setDisplayName('')
    setArchetype('asset_server')
    setMappingText(JSON.stringify(STARTER_MAPPING, null, 2))
    setMatchKeysText(JSON.stringify(STARTER_MATCH_KEYS, null, 2))
    setIdPath('$.id')
    setRecordsRoot('$')
    setEnabled(true)
    setMessage('')
  }

  const startEdit = (s: WebhookSource) => {
    setIsNew(false)
    setEditingName(s.name)
    setRevealedSecret(null)
    setRevealedFor(null)
    setName(s.name)
    setDisplayName(s.display_name)
    setArchetype(s.archetype)
    setMappingText(JSON.stringify(s.mapping || {}, null, 2))
    setMatchKeysText(JSON.stringify(s.match_keys_map || {}, null, 2))
    setIdPath(s.id_path)
    setRecordsRoot(s.records_root)
    setEnabled(s.enabled)
    setMessage('')
  }

  const cancel = () => {
    setIsNew(false)
    setEditingName(null)
    setMessage('')
  }

  const save = async (regenerateSecret = false) => {
    let mapping = {}
    let matchKeys = {}
    try { mapping = JSON.parse(mappingText) } catch (e: any) {
      setMessage(`Mapping is not valid JSON: ${e.message}`); return
    }
    try { matchKeys = JSON.parse(matchKeysText) } catch (e: any) {
      setMessage(`Match keys map is not valid JSON: ${e.message}`); return
    }
    const trimmedName = name.trim()
    if (!trimmedName) { setMessage('Name is required'); return }
    setMessage('Saving…')
    try {
      const { data } = await api.post('/api/cdm/webhook-sources', {
        name: trimmedName,
        display_name: displayName || trimmedName,
        archetype,
        mapping,
        match_keys_map: matchKeys,
        id_path: idPath || '$.id',
        records_root: recordsRoot || '$',
        enabled,
        regenerate_secret: regenerateSecret,
      })
      setMessage(`Webhook source ${data.action}: ${data.name}`)
      if (data.hmac_secret) {
        setRevealedSecret(data.hmac_secret)
        setRevealedFor(data.name)
      }
      setIsNew(false)
      setEditingName(data.name)
      load()
    } catch (e: any) {
      setMessage(`Save failed: ${e.response?.data?.detail || e.message}`)
    }
  }

  const remove = async (n: string) => {
    if (!confirm(`Delete webhook source "${n}"? Inbound POSTs will start returning 404.`)) return
    try {
      await api.delete(`/api/cdm/webhook-sources/${n}`)
      setMessage(`Deleted ${n}`)
      if (editingName === n) cancel()
      load()
    } catch (e: any) {
      setMessage(`Delete failed: ${e.response?.data?.detail || e.message}`)
    }
  }

  const copyToClipboard = (text: string, label: string) => {
    if (!text) return
    if (navigator?.clipboard) {
      navigator.clipboard.writeText(text)
        .then(() => setMessage(`Copied ${label} to clipboard`))
        .catch(() => setMessage('Copy failed — select and copy manually'))
    }
  }

  const curlExample = useMemo(() => {
    if (!editingName && !isNew) return ''
    const target = (isNew ? name.trim() : editingName) || '<source-name>'
    const url = `${apiBase}/api/cdm/webhook/${target}`
    const sample = (() => {
      try { return JSON.parse(mappingText) ? '{ /* match the shape your mapping resolves */ }' : '{}' }
      catch { return '{}' }
    })()
    void sample
    return [
      `# 1. Compute the signature in your sender:`,
      `#    SIG=$(printf '%s' "$BODY" | openssl dgst -sha256 -hmac "$SECRET" | awk '{print $2}')`,
      `# 2. POST it:`,
      `curl -X POST '${url}' \\`,
      `  -H 'Content-Type: application/json' \\`,
      `  -H "X-Signature: sha256=$SIG" \\`,
      `  --data-binary "$BODY"`,
    ].join('\n')
  }, [editingName, isNew, name, mappingText, apiBase])

  const isEditing = isNew || editingName !== null

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <Link to="/app/cdm" className="text-text-muted hover:text-text-primary inline-flex items-center gap-1">
          <ChevronLeft size={14} /> CDM
        </Link>
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Webhook size={18} className="text-cyan" /> CDM WEBHOOK SOURCES
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          push-mode ingest · external systems POST records with HMAC signature
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button type="button" onClick={startNew} className="btn-ghost">
            <Plus size={11} /> New webhook source
          </button>
          <button type="button" onClick={load} disabled={loading} className="btn-ghost">
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {message && (
        <div className="text-[11px] text-text-secondary font-mono px-3 py-2 border border-border rounded bg-surface-card">
          {message}
        </div>
      )}

      {revealedSecret && revealedFor && (
        <div className="border border-emerald/40 bg-emerald/10 rounded p-3 space-y-2">
          <div className="text-[11px] uppercase tracking-widest text-emerald font-semibold">
            New HMAC secret for {revealedFor} — copy now, you will NOT see it again
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 font-mono text-[12px] bg-surface-raised p-2 rounded border border-border break-all">
              {revealedSecret}
            </code>
            <button type="button"
                    onClick={() => copyToClipboard(revealedSecret, 'secret')}
                    className="btn-primary text-[11px]" title="Copy secret">
              <Copy size={11} /> Copy
            </button>
            <button type="button"
                    onClick={() => { setRevealedSecret(null); setRevealedFor(null) }}
                    className="btn-ghost text-[11px]">
              Dismiss
            </button>
          </div>
          <p className="text-[10px] text-text-muted">
            The receiver computes <code>HMAC-SHA256(secret, raw-body)</code> and sends
            the hex digest as <code>X-Signature: sha256=&lt;hex&gt;</code>. We store
            only the secret itself — once dismissed, regenerate to get a new one.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Sources list */}
        <div className="card p-0 overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-surface-raised">
              <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Archetype</th>
                <th className="text-left px-3 py-2">Secret</th>
                <th className="text-center px-3 py-2">On</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {sources.length === 0 && !loading && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-text-muted">
                  No webhook sources yet. Click 'New webhook source' to register one —
                  external systems will POST to <code>/api/cdm/webhook/&lt;name&gt;</code>.
                </td></tr>
              )}
              {sources.map(s => (
                <tr key={s.id} className={clsx(
                  'hover:bg-surface-raised/40',
                  editingName === s.name && 'bg-blue/5',
                )}>
                  <td className="px-3 py-1.5">
                    <button type="button" onClick={() => startEdit(s)}
                            className="text-blue-light hover:underline font-mono text-[11px]">
                      {s.name}
                    </button>
                    <div className="text-[10px] text-text-muted">{s.display_name}</div>
                  </td>
                  <td className="px-3 py-1.5 text-text-secondary">{s.archetype}</td>
                  <td className="px-3 py-1.5 font-mono text-text-secondary">…{s.secret_last4 || '????'}</td>
                  <td className="px-3 py-1.5 text-center">
                    <span className={clsx(
                      'inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-widest',
                      s.enabled ? 'bg-emerald/15 text-emerald' : 'bg-rose/15 text-rose-light',
                    )}>
                      {s.enabled ? 'on' : 'off'}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-right space-x-2">
                    <button type="button"
                            onClick={() => copyToClipboard(`${apiBase}/api/cdm/webhook/${s.name}`, 'webhook URL')}
                            className="text-text-muted hover:text-text-primary"
                            title="Copy webhook URL">
                      <Cable size={12} />
                    </button>
                    <button type="button" onClick={() => remove(s.name)}
                            className="text-rose-light hover:text-rose-light/70" title="Delete">
                      <Trash2 size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Editor */}
        <div className="card p-3 space-y-2">
          <div className="flex items-center gap-2">
            <FileCode size={14} className="text-cyan" />
            <h2 className="text-sm font-semibold text-text-primary">
              {isNew ? 'New webhook source' : editingName ? `Editing: ${editingName}` : 'Webhook editor'}
            </h2>
            {isEditing && (
              <div className="ml-auto flex items-center gap-2">
                {editingName && (
                  <button type="button" onClick={() => save(true)} className="btn-ghost text-[11px]"
                          title="Mint a new secret (the old one stops working immediately)">
                    <RotateCw size={11} /> Regenerate secret
                  </button>
                )}
                <button type="button" onClick={cancel} className="btn-ghost text-[11px]">
                  Cancel
                </button>
                <button type="button" onClick={() => save(false)} className="btn-primary text-[11px]">
                  Save
                </button>
              </div>
            )}
          </div>

          {!isEditing ? (
            <p className="text-text-muted text-xs">
              Click 'New webhook source' to register one, or pick a row to edit.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-text-muted">Name (URL slug)</label>
                  <input value={name}
                         onChange={e => setName(e.target.value)}
                         disabled={!!editingName}
                         placeholder="servicenow-cmdb"
                         className="input w-full font-mono text-[11px]" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-text-muted">Display name</label>
                  <input value={displayName}
                         onChange={e => setDisplayName(e.target.value)}
                         placeholder="ServiceNow CMDB"
                         className="input w-full text-[11px]" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-text-muted">Archetype</label>
                  <select value={archetype}
                          onChange={e => setArchetype(e.target.value)}
                          className="input w-full text-[11px] font-mono">
                    {ARCHETYPES.map(a => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-2 pt-5">
                  <input type="checkbox" id="enabled-chk" checked={enabled}
                         onChange={e => setEnabled(e.target.checked)} />
                  <label htmlFor="enabled-chk" className="text-[11px] text-text-secondary">
                    Enabled (accepting inbound POSTs)
                  </label>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-text-muted">id_path</label>
                  <input value={idPath}
                         onChange={e => setIdPath(e.target.value)}
                         placeholder="$.id"
                         className="input w-full font-mono text-[11px]" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-text-muted">records_root</label>
                  <input value={recordsRoot}
                         onChange={e => setRecordsRoot(e.target.value)}
                         placeholder="$ for one record, $.records for an array"
                         className="input w-full font-mono text-[11px]" />
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-widest text-text-muted">
                  Field mapping (CDM field → JSONPath)
                </label>
                <textarea
                  value={mappingText}
                  onChange={e => setMappingText(e.target.value)}
                  className="w-full font-mono text-[11px] bg-surface-raised p-2 rounded border border-border"
                  rows={6}
                  spellCheck={false}
                />
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-widest text-text-muted">
                  Match keys (CDM field → JSONPath)
                </label>
                <textarea
                  value={matchKeysText}
                  onChange={e => setMatchKeysText(e.target.value)}
                  className="w-full font-mono text-[11px] bg-surface-raised p-2 rounded border border-border"
                  rows={4}
                  spellCheck={false}
                />
              </div>

              <div className="pt-1">
                <div className="flex items-center justify-between">
                  <label className="text-[10px] uppercase tracking-widest text-text-muted">
                    curl example (signature must be computed by sender)
                  </label>
                  <button type="button"
                          onClick={() => copyToClipboard(curlExample, 'curl example')}
                          className="btn-ghost text-[10px]">
                    <Copy size={10} /> Copy
                  </button>
                </div>
                <pre className="text-[10px] font-mono text-text-secondary bg-surface-raised p-2 rounded border border-border overflow-auto">
                  {curlExample}
                </pre>
              </div>
            </>
          )}
        </div>
      </div>

      <p className="text-[10px] text-text-muted text-center">
        Webhook sources let external tools push records to the CDM in real time.
        The receiver verifies <code>HMAC-SHA256(secret, raw-body)</code> on every
        request — invalid signatures get a 401 and never reach the event log.
      </p>
    </div>
  )
}
